import { ChangeDetectionStrategy, Component, computed, effect, inject, Signal, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { distinctUntilChanged, finalize, of, switchMap } from 'rxjs';
import { AnchorDate, AnchorDateContext, AnchorDateService, CalendarMonth, DATE_PERIOD_REGISTRY, DatePeriodConfig, DatePeriodDefinition, DatePeriodKeys, DateRange, DateRangeService } from '../../../../core/date';
import { DatePickerComponent } from '../../../../shared/date-picker/date-picker.component';
import { FieldErrorComponent } from '../../../../shared/field-error/field-error.component';
import { SubscriberSettingsService } from '../../../subscriber';
import { ReportParameterBase } from './report-parameter-base';

export interface DatePeriodParameterOptions {
    startDateParamKey: string;
    startDateLabel?: string;
    endDateParamKey: string;
    endDateLabel?: string;
    datePeriods: DatePeriodConfig[];
    dateRangeMaxYears?: number;
}

interface AnchorDateRequest {
    datePeriodKey: DatePeriodKeys | null;
    anchorDate: AnchorDate;
    context: AnchorDateContext;
}

@Component({
    selector: 'app-date-period-parameter',
    imports: [FormsModule, Select, DatePickerComponent, Tooltip, FieldErrorComponent],
    templateUrl: './date-period-parameter.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DatePeriodParameterComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: DatePeriodParameterComponent,
            multi: true
        }
    ]
})
export class DatePeriodParameterComponent extends ReportParameterBase<DatePeriodKeys | null, DatePeriodParameterOptions> implements Validator {
    private dateRangeService = inject(DateRangeService);
    private subscriberSettingsService = inject(SubscriberSettingsService);
    private anchorDateService = inject(AnchorDateService);

    selectedDatePeriodKey = signal<DatePeriodKeys | null>(null);
    startDate = signal<Date | null>(null);
    endDate = signal<Date | null>(null);

    lastCustomStartDate: Date | null = null;
    lastCustomEndDate: Date | null = null;

    /** Last refresh request handled, so externally applied dates are adopted once per request. */
    private lastHandledRefreshRequest = 0;

    subscriberKey: Signal<number | null> = this.reportWorkspace.subscriberKey;
    private emptyHierarchyNodeKeys = signal<number[]>([]);
    divisionHierarchyNodeKeys: Signal<number[]> = this.reportWorkspace.getParamSignal('divisionHierarchyNodeKeys') ?? this.emptyHierarchyNodeKeys;
    deliveryCenterHierarchyNodeKeys: Signal<number[]> = this.reportWorkspace.getParamSignal('deliveryCenterHierarchyNodeKeys') ?? this.emptyHierarchyNodeKeys;
    serviceCenterHierarchyNodeKeys: Signal<number[]> = this.reportWorkspace.getParamSignal('serviceCenterHierarchyNodeKeys') ?? this.emptyHierarchyNodeKeys;

    datePeriodDefinitions = computed(() => {
        const datePeriods = this.parameterOptions().datePeriods;
        return datePeriods.map((d) => ({ ...DATE_PERIOD_REGISTRY[d.key], ...d.overrides }) as DatePeriodDefinition);
    });

    isFiscalYearStartMonthLoading = signal<boolean>(false);
    fiscalYearStartMonth = toSignal(
        toObservable(this.subscriberKey).pipe(
            switchMap((subscriberKey) => {
                if (subscriberKey == null) {
                    return of(null);
                }

                this.isFiscalYearStartMonthLoading.set(true);

                return this.subscriberSettingsService.getFiscalYearStartMonth(subscriberKey).pipe(finalize(() => this.isFiscalYearStartMonthLoading.set(false)));
            })
        ),
        { initialValue: null }
    );

    /** Publishes one settled request when several saved parameter values are applied together. */
    private anchorDateRequest = computed<AnchorDateRequest>(() => {
        const datePeriodKey = this.selectedDatePeriodKey();
        const datePeriod = this.datePeriodDefinitions().find((d) => d.key === datePeriodKey);
        const anchorDate = datePeriod?.anchorDate ?? AnchorDate.Today;

        return {
            datePeriodKey,
            anchorDate,
            context: {
                subscriberKey: this.subscriberKey(),
                hierarchyNodeKeys: this.getHierarchyNodeKeysForAnchorDate(anchorDate)
            }
        };
    });

    isAnchorDateLoading = signal<boolean>(false);
    anchorDate = toSignal(
        toObservable(this.anchorDateRequest).pipe(
            distinctUntilChanged((previous, current) => this.areAnchorDateRequestsEqual(previous, current)),
            switchMap(({ datePeriodKey, anchorDate, context }) => {
                if (datePeriodKey === 'custom') {
                    return of(new Date());
                }

                this.isAnchorDateLoading.set(true);

                return this.anchorDateService
                    .resolve(anchorDate, context)
                    .pipe(finalize(() => this.isAnchorDateLoading.set(false)));
            })
        ),
        { initialValue: null }
    );
    

    parameterValues = this.reportWorkspace.parameterValues;

    isCustomPeriodSelected = computed(() => this.selectedDatePeriodKey() === 'custom');

    isDateRangeValid = computed(() => {
        if (!this.isCustomPeriodSelected()) {
            return true;
        }

        const startDate = this.startDate();
        const endDate = this.endDate();

        if (startDate == null || endDate == null) {
            return true;
        }

        const maxYears = this.parameterOptions().dateRangeMaxYears ?? 3;
        const diff = Math.abs(endDate.getTime() - startDate.getTime());
        const limit = maxYears * 365 * 24 * 60 * 60 * 1000;

        return diff <= limit;
    });

    datePeriodToolTip = computed(() => {
        const datePeriodKey = this.selectedDatePeriodKey();
        const anchorDate = this.anchorDate();
        const fiscalYearStartMonth = this.fiscalYearStartMonth();

        if (datePeriodKey == null) {
            return '';
        }

        const datePeriodDef = this.datePeriodDefinitions().find((d) => d.key === datePeriodKey);

        if (datePeriodDef?.requiresFiscalStartMonth && fiscalYearStartMonth != null) {
            const test = CalendarMonth[fiscalYearStartMonth!];
            return `Fiscal year starts in ${test}`;
        }

        const anchorDateString = anchorDate?.toLocaleDateString('en-US', {year:'numeric', month: '2-digit', day: '2-digit'});

        const anchorDateTooltipMap: Partial<Record<AnchorDate, string>> = {
            [AnchorDate.LastDeliveryByDeliveryCenter]: anchorDateString ? `Last Delivery Date: ${anchorDateString}` : 'No prior deliveries found',
            [AnchorDate.LastDeliveryByDivision]: anchorDateString ? `Last Delivery Date: ${anchorDateString}` : 'No prior deliveries found',
            [AnchorDate.LastServiceCall]: anchorDateString ? `Last Service Call Date: ${anchorDateString}` : 'No prior service calls found',
            [AnchorDate.LastInvoice]: anchorDateString ? `Last Invoice Date: ${anchorDateString}` : 'No prior invoices found',
        }

        return datePeriodDef?.anchorDate ? anchorDateTooltipMap[datePeriodDef?.anchorDate] ?? '' : '';
    });

    constructor() {
        super();

        /* Recomputes the date range whenever the selected date period, fiscal year start month, or anchor date changes. Additionally anytime a Parameter Refresh or Reset Request is made.*/
        effect(() => {
            const datePeriodKey = this.selectedDatePeriodKey();
            this.fiscalYearStartMonth();
            this.anchorDate();
            const refreshRequest = this.reportWorkspace.parametersRefreshRequest();
            /* Reset nulls the start/end dates in the workspace; recompute them here because a defaulted period key may not change during reset. */
            this.reportWorkspace.parametersResetRequest();

            untracked(() => {
                if (refreshRequest !== this.lastHandledRefreshRequest) {
                    this.lastHandledRefreshRequest = refreshRequest;
                    this.adoptWorkspaceCustomDates();
                }

                this.updateDateRange(datePeriodKey);
            });
        });

        /* Triggers Angular validation whenever the selected period or start/end dates change. */
        effect(() => {
            this.selectedDatePeriodKey();
            this.startDate();
            this.endDate();

            this.onValidatorChange();
        });

        /* Let Workspace know if anything is loading */
        effect(() => {
            const isLoading = this.isFiscalYearStartMonthLoading() || this.isAnchorDateLoading();
            
            this.reportWorkspace.setParamLoading(this.parameterKey(), isLoading);
        });
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();

        const datePeriod = this.selectedDatePeriodKey(); // Defaulted when the FormControl was created.

        /* If a default date period was not provided then try to get the default Start/End date from the parameter defaults */
        if (datePeriod == null || datePeriod == 'custom') {
            const defaultValues = this.reportWorkspace.defaultParameterValues;

            const startDateDefault = (defaultValues[this.parameterOptions().startDateParamKey] as Date) ?? null;
            const endDateDefault = (defaultValues[this.parameterOptions().endDateParamKey] as Date) ?? null;
            const resolvedEndDateDefault = endDateDefault && (!startDateDefault || endDateDefault > startDateDefault) ? endDateDefault : null;

            if (startDateDefault || endDateDefault) {
                this.setDateRange(startDateDefault, resolvedEndDateDefault);

                const datePeriodKey: DatePeriodKeys = 'custom';
                this.selectedDatePeriodKey.set(datePeriodKey);
                this.reportWorkspace.setParam(this.parameterKey(), datePeriodKey);
            }
        }
    }

    onSelectedDatePeriodChange(datePeriodKey: DatePeriodKeys | null) {
        /* Previous Period was Custom -> persist custom dates */
        if (this.selectedDatePeriodKey() === 'custom') {
            this.lastCustomStartDate = this.startDate();
            this.lastCustomEndDate = this.endDate();
        }

        this.selectedDatePeriodKey.set(datePeriodKey);
        this.reportWorkspace.setParam(this.parameterKey(), datePeriodKey);

        this.onChange(datePeriodKey);

        this.markTouchedIfNotResetting();
    }

    onStartDateChange(startDate: Date | null | undefined) {
        const value = startDate ?? null;
        this.startDate.set(value);
        this.reportWorkspace.setParam(this.parameterOptions().startDateParamKey, value);

        this.markTouchedIfNotResetting();
    }

    onEndDateChange(endDate: Date | null | undefined) {
        const value = endDate ?? null;
        this.endDate.set(value);
        this.reportWorkspace.setParam(this.parameterOptions().endDateParamKey, value);

        this.markTouchedIfNotResetting();
    }

    /**
     * Adopts custom start/end dates that were written into the workspace from outside this component.
     *
     * Start and End Date are additional parameters with no control of their own, so applying a saved
     * parameter set writes them straight to the workspace. Without adopting them here, the recompute
     * that follows would write this component's previous custom dates back over the restored ones.
     */
    private adoptWorkspaceCustomDates() {
        const parameterValues = this.reportWorkspace.parameterValues();

        /* Read the applied period from the workspace so this does not depend on the form control having been written yet. */
        if (parameterValues[this.parameterKey()] !== 'custom') {
            return;
        }

        const startDate = (parameterValues[this.parameterOptions().startDateParamKey] as Date | null) ?? null;
        const endDate = (parameterValues[this.parameterOptions().endDateParamKey] as Date | null) ?? null;

        this.startDate.set(startDate);
        this.endDate.set(endDate);
        this.lastCustomStartDate = startDate;
        this.lastCustomEndDate = endDate;
    }

    private updateDateRange(datePeriodKey: DatePeriodKeys | null) {
        const datePeriod = this.datePeriodDefinitions().find((d) => d.key === datePeriodKey);

        const type = datePeriod?.type;
        let dateRange: DateRange | null = null;
        switch (type) {
            case 'CurrentMonth':
                dateRange = this.dateRangeService.getCurrentMonth(this.anchorDate());
                break;
            case 'SingleDate':
                dateRange = this.dateRangeService.getSingleDate(this.anchorDate());
                break;
            case 'YearToDate':
                const startMonth = datePeriod?.requiresFiscalStartMonth ? this.fiscalYearStartMonth() : null;
                dateRange = this.dateRangeService.getYearToDate(this.anchorDate(), startMonth);
                break;
            case 'LastXPeriods':
                dateRange = this.dateRangeService.getLastXPeriods(datePeriod!.periodUnit, datePeriod!.periodCount, this.anchorDate());
                break;
            case 'Custom':
                dateRange = {startDate: this.lastCustomStartDate ?? this.startDate(), endDate: this.lastCustomEndDate ?? this.endDate()}
                break;
            default:
                dateRange = { startDate: null, endDate: null };
        }

        const startDate = dateRange?.startDate ?? null;
        const endDate = dateRange?.endDate ?? null;
        this.setDateRange(startDate, endDate);

    }

    private getHierarchyNodeKeysForAnchorDate(anchorDate: AnchorDate): number[] {
        switch (anchorDate) {
            case AnchorDate.LastDeliveryByDeliveryCenter:
                return this.deliveryCenterHierarchyNodeKeys();
            case AnchorDate.LastDeliveryByDivision:
            case AnchorDate.LastInvoice:
                return this.divisionHierarchyNodeKeys();
            case AnchorDate.LastServiceCall:
                return this.serviceCenterHierarchyNodeKeys();
            default:
                return [];
        }
    }

    private areAnchorDateRequestsEqual(previous: AnchorDateRequest, current: AnchorDateRequest): boolean {
        if (
            previous.datePeriodKey !== current.datePeriodKey ||
            previous.anchorDate !== current.anchorDate ||
            previous.context.subscriberKey !== current.context.subscriberKey
        ) {
            return false;
        }

        const previousKeys = previous.context.hierarchyNodeKeys ?? [];
        const currentKeys = current.context.hierarchyNodeKeys ?? [];

        return previousKeys.length === currentKeys.length && previousKeys.every((key) => currentKeys.includes(key));
    }

    private setDateRange(startDate: Date | null, endDate: Date | null) {
        this.startDate.set(startDate);
        this.reportWorkspace.setParam(this.parameterOptions().startDateParamKey, startDate);

        this.endDate.set(endDate);
        this.reportWorkspace.setParam(this.parameterOptions().endDateParamKey, endDate);
    }

    // CVA callbacks
    writeValue(obj: DatePeriodKeys | null): void {
        this.selectedDatePeriodKey.set(obj);
    }

    // Validator callbacks
    private onValidatorChange = () => {};
    validate(_: AbstractControl): ValidationErrors | null {
        if (this.isCustomPeriodSelected() && (this.startDate() == null || this.endDate() == null)) {
            return { missingDate: true };
        }

        if (!this.isDateRangeValid()) {
            return { exceedsDateRange: true };
        }

        return null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }
}
